import { CHART_DATA_IMPORT, Mixpanel } from 'libs/third-party/Mixpanel';
import { validateSeriesMeta } from 'modules/Editor/components/SideMenuSettings/ChartSettings/ChartSettings.helpers';
import { tableDataToSeriesData } from 'modules/Editor/components/SideMenuSettings/ChartSettings/DataTabTable.helpers';
import { DataTabTableProps } from 'modules/Editor/components/SideMenuSettings/ChartSettings/DataTabTable.types';
import {
  CHART_TABLE_MIN_COLS,
  CHART_TABLE_MIN_ROWS,
} from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import GenericLoader from 'modules/common/components/GenericLoader';
import { Suspense, lazy } from 'react';
import { TableData } from 'types/spreadsheet';
import { appendControlData, extendSeriesData } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { useWidget } from 'widgets/sdk';

const SpreadSheetImport = lazy(
  () =>
    import(
      /* webpackChunkName: "SpreadSheetImport" */
      'modules/common/components/SpreadSheetImport'
    ),
);

interface ImportDataProps {
  headerRowNumber?: number;
  skipColorUpdate?: boolean;
  shouldUpdateDataClasses?: boolean;

  /**
   * The data table may require a set of setting specific to the widget type
   */
  dataTableProps?: Partial<DataTabTableProps>;
}

export const ImportData = ({
  headerRowNumber = 1,
  skipColorUpdate = false,
  shouldUpdateDataClasses = false,
  dataTableProps = {},
}: ImportDataProps) => {
  const {
    colHeaders = true,
    minRows = CHART_TABLE_MIN_ROWS,
    minCols = CHART_TABLE_MIN_COLS,
    shouldAppendControlHeader = false,
  } = dataTableProps;
  const { updateWidget, seriesMeta } = useWidget<ChartWidgetData>();

  // Using the same implementation of updateSeriesDataAndSeriesMeta in DataTabTableContent.tsx
  const onImportData = (tableData: TableData, fileType: string) => {
    const seriesData = tableDataToSeriesData(tableData, headerRowNumber, colHeaders);
    const withControlHeaderData = shouldAppendControlHeader ? appendControlData(seriesData) : seriesData;
    const updatedSeriesData = extendSeriesData(withControlHeaderData, minRows, minCols);

    /*
    validateSeriesMeta handles the checking of each seriesMeta and it
    is extendable to include more seriesMeta by passing in more configs.
    */
    const { shouldUpdateSeriesMeta, updatedSeriesMeta } = validateSeriesMeta({
      colorConfig: { skipColorUpdate, updatedSeriesData, seriesMeta },
      dataClassesConfig: { shouldUpdateDataClasses, updatedSeriesData, seriesMeta },
    });

    if (shouldUpdateSeriesMeta) {
      updateWidget({
        seriesData: updatedSeriesData,
        seriesMeta: updatedSeriesMeta,
      });
    } else {
      updateWidget({ seriesData: updatedSeriesData });
    }

    Mixpanel.track(CHART_DATA_IMPORT, {
      data_type: fileType,
      data_import_status: CHART_DATA_IMPORT.data_import_status.pass,
    });
  };

  const onImportError = () => {
    Mixpanel.track(CHART_DATA_IMPORT, { data_import_status: CHART_DATA_IMPORT.data_import_status.fail });
  };

  return (
    // Rough height of the spreadsheet import
    <Suspense fallback={<GenericLoader />}>
      <SpreadSheetImport onComplete={onImportData} onError={onImportError} />
    </Suspense>
  );
};
